import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Typography } from 'antd';
import {
  CheckCircleFilled,
  LeftOutlined,
  RightOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import ScrollReveal from '../../components/ScrollReveal';
import heroBg from '../../assets/images/home_bg_one.webp';
import heroBg1 from '../../assets/images/home_bg_two.webp';
import heroBg2 from '../../assets/images/home-hero-enterprise.webp';
import heroBg6 from '../../assets/images/hero_bg.webp';
import heroBg8 from '../../assets/images/hero_bg_six.webp';

// Hero 轮播图源 —— 第一张保持原图,第二张用 hero_bg.webp
const heroSlides = [heroBg, heroBg2];
import card1 from '../../assets/images/card_1.png';
import card2 from '../../assets/images/card_2.png';
import card3 from '../../assets/images/card_3.png';
import platformBase from '../../assets/images/platform-base.png';
// 平台 Logo 占位图 - 正式上线请替换为真实 logo 文件
import logo from '../../assets/images/logo.png';
import industryFood from '../../assets/images/industries/industry-food_compressed.webp';
import industryApparel from '../../assets/images/industries/industry-apparel_compressed.webp';
import industryIndustrial from '../../assets/images/industries/industry-industrial_compressed.webp';
import industryDigital from '../../assets/images/industries/industry-digital_compressed.webp';
import industryHome from '../../assets/images/industries/industry-home_compressed.webp';
import industryGeneral from '../../assets/images/industries/industry-general_compressed.webp';
import roleOwner from '../../assets/images/roles/role-owner_compressed.webp';
import roleFinance from '../../assets/images/roles/role-finance_compressed.webp';
import roleSales from '../../assets/images/roles/role-sales_compressed.webp';
import roleWarehouse from '../../assets/images/roles/role-warehouse_compressed.webp';
import rolePurchase from '../../assets/images/roles/role-purchase_compressed.webp';
import roleOperations from '../../assets/images/roles/role-operations_compressed.webp';
import rolesBg from '../../assets/images/roles/roles-bg_compressed.webp';
import iconFood from '../../assets/images/split_images/cut_01.webp';
import iconApparel from '../../assets/images/split_images/cut_02.webp';
import iconIndustrial from '../../assets/images/split_images/cut_03.webp';
import iconDigital from '../../assets/images/split_images/cut_04.webp';
import iconHome from '../../assets/images/split_images/cut_05.webp';
import iconGeneral from '../../assets/images/split_images/cut_06.webp';

const cardImages = [card1, card2, card3];

// ---- 岗位赋能数据 ----
const rolesData = [
  {
    key: 'owner',
    name: '老板',
    title: '经营全局一屏掌握，关键变化及时决策',
    features: [
      '利润、资金、应收应付与库存数据统一查看',
      '销售排行、客户贡献和库存周转快速定位经营重点',
      '业务与财务数据自动核对，经营结果更早呈现',
    ],
    tags: ['经营分析', '利润与资金', '客户贡献'],
    image: roleOwner,
  },
  {
    key: 'finance',
    name: '财务',
    title: '业务自动入账，对账核销与报表更省心',
    features: [
      '业务单据按凭证模板自动生成会计凭证',
      '收付款、应收应付与往来核销集中处理',
      '账簿、利润表和资产负债表随业务持续更新',
    ],
    tags: ['自动凭证', '收付款核销', '账簿报表'],
    image: roleFinance,
  },
  {
    key: 'sales',
    name: '销售',
    title: '报价开单更快，交付回款全程可跟踪',
    features: [
      '销售报价、客户价格与历史成交价随时调用',
      '订单、出库、物流和退货状态沿原单跟踪',
      '客户欠款、收款进度与销售利润清楚可查',
    ],
    tags: ['销售报价', '订单跟踪', '客户对账'],
    image: roleSales,
  },
  {
    key: 'warehouse',
    name: '仓管',
    title: '库存变化有依据，收发盘点更加准确',
    features: [
      '入库、出库、调拨和盘点统一记录库存变化',
      '批次保质期、序列号和商品收发明细持续追踪',
      '库存余额、可用数量与成本调整及时同步',
    ],
    tags: ['库存盘点', '批次管理', '多仓调拨'],
    image: roleWarehouse,
  },
  {
    key: 'purchase',
    name: '采购',
    title: '按需补货，采购成本清楚可控',
    features: [
      '智能补货和以销定购看板辅助确定采购数量',
      '采购历史价、供应商资料与付款情况随单核对',
      '采购订单、入库、退货和到货进度连续跟踪',
    ],
    tags: ['智能补货', '采购历史价', '供应商管理'],
    image: rolePurchase,
  },
  {
    key: 'operations',
    name: '运营',
    title: '经营数据联动，运营决策更精准',
    features: [
      '商品价格本、调价方案与价格变更统一管理',
      '客户分层、贡献分析和销售排行识别经营机会',
      '销售利润、库存周转与订单数据辅助持续优化',
    ],
    tags: ['价格管理', '客户经营', '数据分析'],
    image: roleOperations,
  },
];

const { Title, Paragraph } = Typography;

// ---- 业财协同 - 流程卡片数据 ----
const processCards = [
  {
    type: 'purchase',
    title: '采购入库',
    subtitle: '库存增多',
    subtitleColor: '#1890ff',
    // 小项底色 — 蓝
    itemBg: '#f0f5ff',
    itemBorder: '#d6e4ff',
    items: ['智能补货', '购货订单', '入库退货'],
    connector: '入库',
  },
  {
    type: 'inventory',
    title: '库存管理',
    subtitle: '实时同步',
    subtitleColor: '#1890ff',
    // 小项底色 — 蓝(与采购一致,都属于库存链条)
    itemBg: '#f0f5ff',
    itemBorder: '#d6e4ff',
    items: ['实时库存', '多仓调拨', '批次盘点'],
    connector: '出库',
  },
  {
    type: 'sales',
    title: '销售出库',
    subtitle: '库存减少',
    subtitleColor: '#52c41a',
    // 小项底色 — 绿
    itemBg: '#EDF9F8',
    itemBorder: '#d7ece8',
    items: ['销售报价', '订单出库', '退货跟踪'],
    connector: '入账',
    connectorModifier: 'finance',
  },
  {
    type: 'finance',
    title: '财务管理',
    subtitle: '数据同源',
    subtitleColor: '#722ed1',
    // 小项底色 — 紫
    itemBg: '#f4f1fd',
    itemBorder: '#fefdff',
    items: ['应收应付', '收付款核销', '自动凭证'],
    connector: null,
  },
];

// ---- 行业方案 ----
const industries = [
  {
    key: 'food',
    name: '食品酒水',
    icon: iconFood,
    bg: industryFood,
    title: '效期提前预警，渠道利润与回款同步看清',
    features: [
      '批次保质期清单与批次跟踪，临期商品及时处理',
      '智能补货结合销售和库存，降低断货与积压',
      '销售利润、客户欠款对账单统一查看',
    ],
    buttons: ['批次保质期', '智能补货', '客户对账'],
  },
  {
    key: 'apparel',
    name: '服装鞋帽',
    icon: iconApparel,
    bg: industryApparel,
    title: '多属性商品管得细，畅销款与利润看得清',
    features: [
      '辅助属性管理颜色、尺码、款式，查货开单更准确',
      '价格本与调价方案统一售价，价格变化可追溯',
      '销售排行榜与销售利润表识别畅销款和高毛利商品',
    ],
    buttons: ['辅助属性', '商品价格成本', '销售排行'],
  },
  {
    key: 'industrial',
    name: '工业用品',
    icon: iconIndustrial,
    bg: industryIndustrial,
    title: '快速找货、准确报价、及时交付',
    features: [
      '客户物料编码关联型号规格，查找和下单更高效',
      '销售报价、采购历史价与商品价格本辅助准确报价',
      '销售订单跟踪与序列号状态，交付进度和货品去向可查',
    ],
    buttons: ['客户物料编码', '销售报价', '订单跟踪'],
  },
  {
    key: 'digital',
    name: '数码3C',
    icon: iconDigital,
    bg: industryDigital,
    title: '一机一码可追溯，价格利润与退换更清楚',
    features: [
      '序列号跟踪与状态查询，明确每台设备进销去向',
      '销货退货关联订单，问题设备与原销售记录可核对',
      '价格本、调价方案和销售利润表支撑价格与毛利管理',
    ],
    buttons: ['序列号跟踪', '销货退货', '销售利润'],
  },
  {
    key: 'home',
    name: '家居日用',
    icon: iconHome,
    bg: industryHome,
    title: '品类多、周转快，采购补货与履约更从容',
    features: [
      '商品类别、辅助属性和条码规则统一大量商品资料',
      '智能补货与采购订单跟踪，及时补齐畅销商品',
      '销售订单跟踪、物流服务与客户对账集中协同',
    ],
    buttons: ['商品管理', '智能补货', '订单跟踪'],
  },
  {
    key: 'general',
    name: '通用行业',
    icon: iconGeneral,
    bg: industryGeneral,
    title: '采购、销售、资金与财务在一套系统中闭环',
    features: [
      '采购、销售、库存与收付款单据沿业务流程贯通',
      '业务单据自动生成凭证，并可核对进销存数据',
      '利润表、应收应付和客户贡献形成经营看板',
    ],
    buttons: ['业财一体化', '自动凭证', '经营分析'],
  },
];

// ---- 数字滚动组件 ----
const CountUp = ({ target, unit, label, duration = 2000 }) => {
  const [current, setCurrent] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;

    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCurrent(Math.round(eased * target));

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }, [started, target, duration]);

  const formatted = current.toLocaleString();

  return (
    <div className="stat-item" ref={ref}>
      <div className="stat-number">
        {formatted}
        <span className="stat-unit">{unit}</span>
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
};

// ---- 首页主组件 ----
const Home = () => {
  const [activeIndustry, setActiveIndustry] = useState(industries[0].key);
  const [activeRole, setActiveRole] = useState(0);
  // Hero 轮播 —— 当前展示第几张,默认 0
  const [heroIndex, setHeroIndex] = useState(0);

  // 预加载 6 张行业背景图，避免切换 tab 时短暂闪遮罩
  useEffect(() => {
    industries.forEach((ind) => {
      const img = new Image();
      img.src = ind.bg;
    });
  }, []);

  // Hero 自动轮播 —— 5s 切换一张,组件卸载时清理 timer
  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((i) => (i + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const stats = [
    { target: 2000, unit: '+', label: '服务企业客户' },
    { target: 30, unit: '+', label: '覆盖行业领域' },
    { target: 80, unit: '%', label: '成本节约' },
    { target: 70, unit: '%', label: '工作效率提升' },
  ];

  return (
    <div className="page-home">
      {/* ======== Hero — 全幅背景图固定铺满视口(顶上去),内容覆盖其上 ======== */}
      <section className="hero-section">
        {/* 轮播背景层 —— 多张图叠加,通过 opacity 切换。
           改用 <img> 而非 background-image:让图片本身决定 hero 高度,
           永远按原图比例完整显示,不会出现 contain 模式下的上下留白
           或 cover 模式下的裁切 */}
        <div className="hero-carousel">
          {heroSlides.map((src, idx) => (
            <img
              key={idx}
              src={src}
              alt=""
              className={`hero-bg${
                idx === heroIndex ? ' hero-bg--active' : ''
              }`}
            />
          ))}
        </div>
        <div className="hero-container">
          {/* 「业财一体化」标题只展示在第一张图上 */}
          {heroIndex === 0 && (
            <div className="hero-text">
              <Title level={1} className="hero-title hero-title--single">
                
              </Title>
            </div>
          )}
        </div>

        {/* 左右切换箭头已移除 —— 仅保留自动轮播 + 底部双箭头引导 */}

        {/* 右下角轮播指示条 —— 两条短横线,可点击切换,当前激活那条拉长 + 品牌红 */}
        <div className="hero-carousel-indicator" aria-label="轮播进度">
          {heroSlides.map((_, idx) => (
            <button
              key={idx}
              type="button"
              className={`hero-carousel-indicator-bar${
                idx === heroIndex ? ' hero-carousel-indicator-bar--active' : ''
              }`}
              onClick={() => setHeroIndex(idx)}
              aria-label={`切换到第 ${idx + 1} 张`}
            />
          ))}
        </div>

        {/* 底部向下双箭头 —— 两张图共用,引导用户向下浏览 */}
        <div className="hero-scroll-hint" aria-hidden="true">
          <svg viewBox="0 0 36 24" width="64" height="44">
            <polyline
              className="hero-scroll-chevron hero-scroll-chevron--top"
              points="4,3 18,13 32,3"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polyline
              className="hero-scroll-chevron hero-scroll-chevron--bottom"
              points="4,11 18,21 32,11"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </section>

      {/* ======== 数据统计区 ======== */}
      <section className="stats-section">
        <div className="section-container">
          <ScrollReveal>
            <Row gutter={[24, 24]}>
              {stats.map((item, idx) => (
                <Col xs={12} md={6} key={idx}>
                  <CountUp target={item.target} unit={item.unit} label={item.label} duration={2000} />
                </Col>
              ))}
            </Row>
          </ScrollReveal>
        </div>
      </section>

      {/* ======== 核心价值 ======== */}
      <section className="value-section">
        <div className="section-container">
          <ScrollReveal>
            <Title level={2} className="value-section-title">核心价值</Title>
            <div className="value-title-underline" />
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <Row gutter={[24, 24]}>
              {[
                {
                  title: '业财同源',
                  desc: '销货单、购货单、收付款单一经审核，自动按模板生成会计凭证。业务账与财务账同出一源，从此告别"两套账、对不上"。',
                  color: 'pink',
                },
                {
                  title: '智能提效',
                  desc: '发票识别、银行流水自动对账、智能补货建议、批量打印开单，把人从重复劳动里解放出来，专注于经营本身。',
                  color: 'orange',
                },
                {
                  title: '数据决策',
                  desc: '销售排行、客户贡献、库存周转、利润趋势实时可视。老板打开手机，就能看清公司今天在赚钱还是亏钱。',
                  color: 'red',
                },
              ].map((item, idx) => (
                <Col xs={24} md={8} key={idx}>
                  <div
                    className="value-card"
                    style={{ backgroundImage: `url(${cardImages[idx]})` }}
                  >
                    <div className="value-card-overlay">
                      <Title level={4} className="value-card-title">{item.title}</Title>
                      <Paragraph className="value-card-desc">{item.desc}</Paragraph>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </ScrollReveal>
        </div>
      </section>

      {/* ======== 业财协同 ======== */}
      <section className="home-process-section">
        <div className="section-container">
          <ScrollReveal>
            <div className="section-head home-section-head">
              <Title level={2} className="section-only-title">业财协同</Title>
              <div className="home-process-title-underline" />
            </div>
          </ScrollReveal>

          <ScrollReveal delay={200}>
          <div className="home-process-map">
            <div className="home-process-map__main">
              {processCards.map((card, idx) => (
                <React.Fragment key={idx}>
                  <article
                    className={`home-process-card home-process-card--${card.type}`}
                  >
                    <h3>
                      {card.title}
                      <span style={{ color: card.subtitleColor }}>
                        {card.subtitle}
                      </span>
                    </h3>
                    <ul className="home-process-card__actions">
                      {card.items.map((item, i) => (
                        <li
                          key={i}
                          style={{
                            background: card.itemBg,
                            borderColor: card.itemBorder,
                          }}
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </article>

                  {card.connector && (
                    <div
                      className={
                        'home-process-connector' +
                        (card.connectorModifier
                          ? ` home-process-connector--${card.connectorModifier}`
                          : '')
                      }
                    >
                      <svg
                        className="home-process-connector__svg"
                        viewBox="0 0 100 60"
                        preserveAspectRatio="xMidYMid meet"
                      >
                        <defs>
                          <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#174AC9" />
                            <stop offset="100%" stopColor="#3478F4" />
                          </linearGradient>
                          <linearGradient id="arrowGradientFinance" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#5B21B6" />
                            <stop offset="100%" stopColor="#9333EA" />
                          </linearGradient>
                        </defs>
                        <polygon
                          className="home-process-connector__shape"
                          points="0,8 70,8 70,0 100,30 70,60 70,52 0,52"
                        />
                        <text
                          className="home-process-connector__label"
                          x="36"
                          y="31"
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          {card.connector}
                        </text>
                      </svg>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="home-process-platforms" aria-label="业务协同平台">
              <div className="home-process-platforms__base" aria-hidden="true">
                <img src={platformBase} alt="" loading="lazy" decoding="async" />
              </div>
            </div>

            <div className="home-platform-card-standalone" aria-label="业务协同平台品牌">
              <article className="home-platform-card">

                <h3 className="home-platform-card__title">我们将为您智能生成方案，让业财协同更简单</h3>
              </article>
            </div>
          </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ======== 行业方案 ======== */}
      <section className="industry-section">
        <div className="industry-wrapper">
        <div className="section-container">
          <ScrollReveal>
            <Title level={2} className="industry-section-title">行业方案</Title>
            <div className="industry-title-underline" />
          </ScrollReveal>
        </div>

        <ScrollReveal delay={200}>
        {(() => {
          const current =
            industries.find((i) => i.key === activeIndustry) || industries[0];
          return (
            <div
              className="industry-panel"
              style={{ '--industry-bg': `url(${current.bg})` }}
            >
              <div className="industry-panel-inner">
                <div className="industry-tabs" role="tablist">
                  {industries.map((ind) => (
                    <button
                      key={ind.key}
                      role="tab"
                      aria-selected={activeIndustry === ind.key}
                      className={
                        'industry-tab' +
                        (activeIndustry === ind.key ? ' active' : '')
                      }
                      onClick={() => setActiveIndustry(ind.key)}
                      onMouseEnter={() => setActiveIndustry(ind.key)}
                    >
                      <span className="industry-tab-icon">
                        <img src={ind.icon} alt={ind.name} loading="lazy" decoding="async" />
                      </span>
                      <span className="industry-tab-name">{ind.name}</span>
                    </button>
                  ))}
                </div>

                <div className="industry-content">
                  <Title level={3} className="industry-content-title">
                    {current.title}
                  </Title>
                  <ul className="industry-features">
                    {current.features.map((f, i) => (
                      <li key={i}>
                        <CheckCircleFilled />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="industry-buttons">
                    {current.buttons.map((b, i) => (
                      <button key={i} className="industry-btn" type="button">
                        {b}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              </div>
            );
          })()}
        </ScrollReveal>
        </div>
      </section>

      {/* ======== 岗位赋能 ======== */}
      <section
        className="roles-section"
        style={{ backgroundImage: `url()` }}
      >
        <div className="section-container">
          <ScrollReveal>
            <Title level={2} className="roles-section-title">岗位赋能</Title>
            <div className="roles-title-underline" />
          </ScrollReveal>

          <div className="roles-panel-wrapper">
              {(() => {
                const current = rolesData[activeRole];
                const prevRole = () => setActiveRole((i) => (i - 1 + rolesData.length) % rolesData.length);
                const nextRole = () => setActiveRole((i) => (i + 1) % rolesData.length);

                return (
                  <>
                    {/* 主面板 — ScrollReveal 放在面板自身,避免父级 transform 期间 backdrop-filter 被跳过 */}
                    <ScrollReveal delay={200}>
                      <div className="roles-main-panel">
                      <button className="roles-nav roles-nav-prev" onClick={prevRole}>
                        <LeftOutlined />
                      </button>

                      <div className="roles-main-inner">
                        {/* 左侧文字 */}
                        <div className="roles-left">
                          <h3 className="roles-left-title">{current.title}</h3>
                          <ul className="roles-left-features">
                            {current.features.map((f, i) => (
                              <li key={i}>
                                <CheckCircleOutlined />
                                <span>{f}</span>
                              </li>
                            ))}
                          </ul>
                          <div className="roles-left-tags">
                            {current.tags.map((t, i) => (
                              <span key={i} className="roles-tag">{t}</span>
                            ))}
                          </div>
                        </div>

                        {/* 右侧人物大图 */}
                        <div className="roles-right">
                          <img src={current.image} alt={current.name} loading="lazy" decoding="async" />
                          <div className="roles-right-name">{current.name}</div>
                        </div>
                      </div>

                      <button className="roles-nav roles-nav-next" onClick={nextRole}>
                        <RightOutlined />
                      </button>

                      {/* 底部角色切换条 */}
                      <div className="roles-tabs">
                        {rolesData.map((role, idx) => (
                          <button
                            key={role.key}
                            className={`roles-tab${activeRole === idx ? ' active' : ''}`}
                            onClick={() => setActiveRole(idx)}
                          >
                            <img src={role.image} alt={role.name} loading="lazy" decoding="async" />
                            <span>{role.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    </ScrollReveal>
                  </>
                );
              })()}
            </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
